import i18n from '@/i18n';
import {
  FollowUpStatus,
  MessageRecipientStatus,
  MessageStatus,
} from '@/types/components';
import type {
  MailDraftRecipientResponse,
  MailDraftResponse,
  MailMessage,
  MailMessageResponse,
  MailRecipient,
  MailRecipientResponse,
  Recipient,
} from '@/types/mail';
import { formatDayShortMonthYear } from '@/utils/date';
import { isNumeric, stripHtml } from '@/utils/text';

export const SYSTEM_AUTHOR_USER_ID = 0;

const getSenderName = (response: MailMessageResponse): string => {
  if (response.authorUserId === SYSTEM_AUTHOR_USER_ID) {
    return i18n.t('mail.systemMessage');
  }

  return response.authorName ?? '';
};

export const toMailRecipient = (
  recipient: MailRecipientResponse,
): MailRecipient => ({
  id: String(recipient.id),
  name: recipient.name,
  status: recipient.status ?? MessageRecipientStatus.Unread,
});

const toDraftRecipient = (
  recipient: MailDraftRecipientResponse,
): MailRecipient => ({
  id: String(recipient.id),
  name: recipient.description
    ? `${recipient.name} ${recipient.description}`
    : recipient.name,
  status: MessageRecipientStatus.Unread,
});

const hasGroupId = (recipient: MailDraftRecipientResponse) =>
  !!recipient.groupId && isNumeric(recipient.groupId);

const decodeDraftRecipients = (
  draft: MailDraftResponse,
): MailDraftRecipientResponse[] => {
  if (!draft.recipients) {
    return [];
  }

  try {
    return JSON.parse(draft.recipients) as MailDraftRecipientResponse[];
  } catch {
    return [];
  }
};

const toRecipient = (recipient: MailDraftRecipientResponse): Recipient => ({
  id: recipient.id,
  groupId: hasGroupId(recipient) ? Number(recipient.groupId) : undefined,
  name: recipient.name,
  description: recipient.description ?? '',
});

export const toMailMessage = (response: MailMessageResponse): MailMessage => {
  const {
    hideRecipients,
    loadRecipientsSeparately,
    recipients: allRecipients = [],
  } = response.recipientsData ?? {};
  const messageRecipients = hideRecipients
    ? []
    : allRecipients.map(toMailRecipient);
  const draftRecipients = response.draft
    ? decodeDraftRecipients(response.draft)
    : [];
  const recipients = response.draft
    ? draftRecipients.filter(hasGroupId).map(toDraftRecipient)
    : messageRecipients;
  const [firstRecipient, ...otherRecipients] = recipients;
  const isSingleRecipient = recipients.length === 1;

  return {
    id: String(response.id),
    senderName: getSenderName(response),
    authorUserId: response.authorUserId,
    recipientName: firstRecipient?.name,
    additionalRecipientCount: otherRecipients.length,
    recipientStatus: isSingleRecipient ? firstRecipient.status : null,
    subject: response.subject,
    preview: stripHtml(response.body),
    date: formatDayShortMonthYear(response.timeCreated),
    timeCreated: response.timeCreated,
    loadRecipientsSeparately: !!loadRecipientsSeparately && !hideRecipients,
    status: response.status ?? MessageStatus.Read,
    followUpStatus: response.followUpStatus ?? FollowUpStatus.None,
    hasAttachment: !!response.attachments?.length,
    body: response.body,
    recipients,
    draftRecipients: draftRecipients.map(toRecipient),
    draftMessageType: response.draft?.messageType,
    draftPreviousMessageId: response.draft?.previousMessageId,
    attachments: response.attachments ?? [],
  };
};
